// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * React hook module for managing fulfillment order lists with filtering capabilities.
 * Provides a convenient interface for fetching and filtering fulfillment order data.
 */
import { locationCompanyTypeSchema } from '@/constants/location-company-type';
import { sortDirectionSchema } from '@/constants/sort';
import { getFulfillments } from '@/fulfillment/api/get-fulfillments';
import { FulfillmentFilter } from '@/fulfillment/api/types';
import {
  fulfillmentOrderPhaseSchema,
  fulfillmentOrderSubstatusSchema,
} from '@/fulfillment/constants/fulfillment-order-status';
import { fulfillmentListQueryKey } from '@/fulfillment/query-keys';
import { searchTermSchema } from '@/utilities/search-term';
import { useSuspenseQuery } from '@tanstack/react-query';
import { z } from 'zod';

/**
 * Configuration options for the useFulfillments hook.
 */
interface UseFulfillmentsOptions {
  filter?: FulfillmentFilter;
}

/**
 * React hook for fetching and managing fulfillment order lists with filtering.
 * Uses React Query's suspense mode for data fetching.
 *
 * Features:
 * - Automatic data fetching and caching via React Query
 * - Filtering support via FulfillmentFilter options
 * - Suspense mode for loading states
 *
 * @param options - Configuration object for the hook
 *
 * @returns Object containing:
 * - fulfillments: Filtered array of fulfillment orders
 * - resultCount: Total count of results
 * - Query status information (error, refetch, etc.)
 *
 * @throws Will suspend while loading data
 * @throws Will throw errors from the API if request fails
 */
export function useFulfillments(options: UseFulfillmentsOptions = {}) {
  // Set up React Query with suspense mode
  const { data, ...query } = useSuspenseQuery({
    queryKey: fulfillmentListQueryKey(options.filter),
    queryFn: () => getFulfillments(options.filter),
    refetchOnWindowFocus: true,
  });

  const fulfillments = data.fulfillments;
  const resultCount = data.resultCount;
  return { fulfillments, resultCount, ...query };
}

/**
 * Sort property options for fulfillment orders
 */
const fulfillmentSortPropertySchema = z.enum([
  'foNumber',
  'status',
  'cycleTime',
]);

export type FulfillmentSortProperty = z.infer<
  typeof fulfillmentSortPropertySchema
>;

/**
 * Search schema for fulfillment listing page.
 *
 * The from/to location filters mirror the asset list location filter and
 * support three independent dimensions per side: explicit location IDs,
 * customer IDs (which select all of that customer's locations), and
 * location company types (which select all manufacturer or customer
 * locations as a group).
 */
export const fulfillmentSearchListingSchema = z.object({
  query: searchTermSchema,
  status: fulfillmentOrderPhaseSchema.optional(),
  subStatuses: z.array(fulfillmentOrderSubstatusSchema).optional(),
  page: z.number().optional(),
  sort: fulfillmentSortPropertySchema.optional(),
  direction: sortDirectionSchema.optional(),
  forProjectId: z.string().optional(),
  fromLocations: z.array(z.string()).optional(),
  fromCustomerLocations: z.array(z.string()).optional(),
  fromLocationCompanyTypes: z
    .array(locationCompanyTypeSchema)
    .optional()
    .catch([]),
  toLocations: z.array(z.string()).optional(),
  toCustomerLocations: z.array(z.string()).optional(),
  toLocationCompanyTypes: z
    .array(locationCompanyTypeSchema)
    .optional()
    .catch([]),
});

export type FulfillmentSearchListing = z.infer<
  typeof fulfillmentSearchListingSchema
>;
